import { Divider, List, Typography } from '@pulse/ui';
import { Fragment } from 'react';

import type { NavDomain, NavGroup, NavLeaf, NavNode } from '../types/nav.types';

import { NavGroupHeader } from './NavGroupHeader';
import { NavItem } from './NavItem';

interface SideNavProps {
  nav: NavNode[];
  activePath: string;
  onNavigate: (path: string) => void;
  // Icon-rail mode — Domain/Group text headers disappear (no icon to show) and only Page
  // icons render, flattened, with dividers between top-level sections.
  collapsed: boolean;
}

function isDomain(node: NavNode): node is NavDomain {
  return node.kind === 'domain';
}

function flattenLeaves(children: (NavGroup | NavLeaf)[]): NavLeaf[] {
  return children.flatMap((child) => (child.kind === 'group' ? child.children : [child]));
}

export function SideNav({ nav, activePath, onNavigate, collapsed }: SideNavProps) {
  return (
    <List disablePadding>
      {nav.map((node, index) => {
        const isLast = index === nav.length - 1;

        if (!isDomain(node)) {
          // Top-level page with no domain (e.g. Home).
          return (
            <NavItem
              key={node.id}
              icon={node.icon}
              label={node.label}
              active={activePath === node.path}
              collapsed={collapsed}
              onClick={() => onNavigate(node.path)}
            />
          );
        }

        if (collapsed) {
          return (
            <Fragment key={node.id}>
              {flattenLeaves(node.children).map((leaf) => (
                <NavItem
                  key={leaf.id}
                  icon={leaf.icon}
                  label={leaf.label}
                  active={activePath === leaf.path}
                  collapsed
                  onClick={() => onNavigate(leaf.path)}
                />
              ))}
              {!isLast && <Divider sx={{ my: 1 }} />}
            </Fragment>
          );
        }

        return (
          <Fragment key={node.id}>
            <Typography
              variant="caption"
              sx={{ display: 'block', px: 2, pt: 2, pb: 0.5, fontWeight: 700, opacity: 0.6 }}
            >
              {node.label.toUpperCase()}
            </Typography>
            {node.children.map((child) =>
              child.kind === 'group' ? (
                <NavGroupHeader key={child.id} label={child.label}>
                  {child.children.map((leaf) => (
                    <NavItem
                      key={leaf.id}
                      icon={leaf.icon}
                      label={leaf.label}
                      active={activePath === leaf.path}
                      collapsed={false}
                      onClick={() => onNavigate(leaf.path)}
                    />
                  ))}
                </NavGroupHeader>
              ) : (
                <NavItem
                  key={child.id}
                  icon={child.icon}
                  label={child.label}
                  active={activePath === child.path}
                  collapsed={false}
                  onClick={() => onNavigate(child.path)}
                />
              ),
            )}
          </Fragment>
        );
      })}
    </List>
  );
}
